import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { tenantCommand } from '../../model/const/command';
import { LeaseAgreementCategory, RentalObjectCategory, TenantCategory, TenantCategoryToString } from '../../model/const/enums';
import { Contact, ContactIdKind } from '../../model/contact';
import { Contract } from '../../model/contract';
import { PrintGdprCommandHandler } from '../../service/command/print-gdpr-command-handler.service';
import { SendRegistrationKeyCommandHandler } from '../../service/command/send-registration-key-command-handler.service';
import { TenantFormCommandHandlerResolver } from '../../service/command/tenant-form-command-handler-resolver.service';
import { ContactService } from '../../service/data/contact.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { RentApplication } from '../../model/rent-application';
import { catchError, pairwise, startWith, switchMap, tap } from 'rxjs/operators';
import { RepayBalanceCommandHandler } from '../../service/command/repay-balance-command-handler.service';
import { of, Subscription, throwError } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { BrfTransferForm } from '../../model/Transfer/transfer-form';
import { CooperativeHousingTransferFormService } from '../../service/data/cooperative-housing-transfer-form.service';
import { TransferType, transferTypeToString } from '../../model/const/transfer-type';
import { ControlDataExportForm } from '../../model/control-data/control-data-export-form';
import { ControlDataExportFormService } from '../../service/data/control-data-export-form.service';
import { ControlDataForm } from '../../model/control-data/control-data-form';
import { CompanyService } from '../../service/data/company.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { ControlDataFormService } from '../../service/data/control-data-form.service';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { KU55Content } from '../../model/control-data/ku55-content';
import { Money } from '../../../core/model/money';

@Component({
    selector: 'control-data-55-form',
    templateUrl: './control-data-55-form.component.html',
    styleUrls: ['./control-data-55-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        NavigationService,
        FormCommandHandlerResolver,
        { provide: DataService, useExisting: ControlDataFormService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ControlData55FormComponent extends FormComponentBase<ControlDataForm> implements OnDestroy {
  constructor(
    private _cf: FormControlExFactory,
    private fb: FormBuilder,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    dataService: ControlDataFormService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, dataService, messageService, navigation, dispatcher, handlerResolver);
    this.menuDefinition = [];
  }

  createModel(): ControlDataForm {
    return new ControlDataForm();
  }

  individualSelection: SelectionItem[] = [
    new SelectionItem('COOPERATIVE_HOUSING.KU55.646_SELECTION_INDIVIDUAL', 'I'),
    new SelectionItem('COOPERATIVE_HOUSING.KU55.646_SELECTION_COMMON', 'G'),
  ];

  contentForm: FormGroup;
  protected getFormGroup(): FormGroup {
    let form = this.dataContext;
    this.contentForm = this.fb.group({
      id: this._cf.createHidden(form.id),
      fK076_LandskodTIN: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.076_LANDSKODTIN',
        form.kU55Content.fK076_LandskodTIN,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.076_LANDSKODTIN'
      ),
      fK205_Borttag: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.205_BORTTAG',
        form.kU55Content.fK205_Borttag,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.205_BORTTAG'
      ),
      fK210_Rattelse: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.210_RATTELSE',
        form.kU55Content.fK210_Rattelse,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.210_RATTELSE'
      ),
      fK215_Inkomsttagare: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.215_INKOMSTTAGARE',
        form.kU55Content.fK215_Inkomsttagare,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.215_INKOMSTTAGARE'
      ),
      fK216_Fornamn: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.216_FORNAMN',
        form.kU55Content.fK216_Fornamn,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.216_FORNAMN'
      ),
      fK217_Efternamn: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.217_EFTERNAMN',
        form.kU55Content.fK217_Efternamn,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.217_EFTERNAMN'
      ),
      fK218_Gatuadress: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.218_GATUADRESS',
        form.kU55Content.fK218_Gatuadress,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.218_GATUADRESS'
      ),
      fK219_Postnummer: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.219_POSTNUMMER',
        form.kU55Content.fK219_Postnummer,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.219_POSTNUMMER'
      ),
      fK220_Postort: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.220_POSTORT',
        form.kU55Content.fK220_Postort,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.220_POSTORT'
      ),
      fK221_LandskodPostort: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.221_LANDSKODPOSTORT',
        form.kU55Content.fK221_LandskodPostort,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.221_LANDSKODPOSTORT'
      ),
      fK222_Fodelsetid: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.222_FODELSETID',
        form.kU55Content.fK222_Fodelsetid,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.222_FODELSETID'
      ),
      fK224_AnnatIDNr: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.224_ANNATIDNR',
        form.kU55Content.fK224_AnnatIDNr,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.224_ANNATIDNR'
      ),
      fK226_OrgNamn: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.226_ORGNAMN',
        form.kU55Content.fK226_OrgNamn,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.226_ORGNAMN'
      ),
      fK228_Gatuadress2: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.228_GATUADRESS2',
        form.kU55Content.fK228_Gatuadress2,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.228_GATUADRESS2'
      ),
      fK230_FriAdress: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.230_FRIADRESS',
        form.kU55Content.fK230_FriAdress,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.230_FRIADRESS'
      ),
      fK252_TIN: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.252_TIN',
        form.kU55Content.fK252_TIN,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.252_TIN'
      ),
      fK630_BostRattBeteckning: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.630_BOSTRATTBETECKNING',
        form.kU55Content.fK630_BostRattBeteckning,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.630_BOSTRATTBETECKNING'
      ),
      fK631_Overlatelsedatum: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.631_OVERLATELSEDATUM',
        form.kU55Content.fK631_Overlatelsedatum,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.631_OVERLATELSEDATUM'
      ),
      fK632_OverlatenAndel: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.632_OVERLATENANDEL',
        form.kU55Content.fK632_OverlatenAndel,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.632_OVERLATENANDEL'
      ),
      fK633_OverlatelseArvBodeln: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.633_OVERLATELSEARVBODELN',
        form.kU55Content.fK633_OverlatelseArvBodeln,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.633_OVERLATELSEARVBODELN'
      ),
      fK634_Overlatelsepris: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.634_OVERLATELSEPRIS',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK634_Overlatelsepris ? form.kU55Content.fK634_Overlatelsepris.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.634_OVERLATELSEPRIS'
      ),
      fK635_InreRepFondOverlatelse: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.635_INREREPFONDOVERLATELSE',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK635_InreRepFondOverlatelse ? form.kU55Content.fK635_InreRepFondOverlatelse.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.635_INREREPFONDOVERLATELSE'
      ),
      fK636_Kapitaltillskott: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.636_KAPITALTILLSKOTT',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK636_Kapitaltillskott ? form.kU55Content.fK636_Kapitaltillskott.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.636_KAPITALTILLSKOTT'
      ),
      fK638_OaktaBostadsftg: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.638_OAKTABOSTADSFTG',
        form.kU55Content.fK638_OaktaBostadsftg,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.638_OAKTABOSTADSFTG'
      ),
      fK639_Tillaggskopeskilling: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.639_TILLAGGSKOPESKILLING',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK639_Tillaggskopeskilling ? form.kU55Content.fK639_Tillaggskopeskilling.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.639_TILLAGGSKOPESKILLING'
      ),
      fK640_Forvarvsdatum: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.640_FORVARVSDATUM',
        form.kU55Content.fK640_Forvarvsdatum,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.640_FORVARVSDATUM'
      ),
      fK642_ForvarvArvBodeln: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.642_FORVARVARVBODELN',
        form.kU55Content.fK642_ForvarvArvBodeln,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.642_FORVARVARVBODELN'
      ),
      fK643_Forvarvspris: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.643_FORVARVSPRIS',
        new Money(form.kU55Content.currencyCode, form.kU55Content.fK643_Forvarvspris ? form.kU55Content.fK643_Forvarvspris.amount : null),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.643_FORVARVSPRIS'
      ),
      fK644_InreRepFondForvarv: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.644_INREREPFONDFORVARV',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK644_InreRepFondForvarv ? form.kU55Content.fK644_InreRepFondForvarv.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.644_INREREPFONDFORVARV'
      ),
      fK645_AndelBRFFormogenhet1974: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.645_ANDELBRFFORMOGENHET1974',
        new Money(
          form.kU55Content.currencyCode,
          form.kU55Content.fK645_AndelBRFFormogenhet1974 ? form.kU55Content.fK645_AndelBRFFormogenhet1974.amount : null
        ),
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.645_ANDELBRFFORMOGENHET1974'
      ),
      fK646_GemensamIndividuell: this._cf.create(
        'COOPERATIVE_HOUSING.KU55.FK.646_GEMENSAMINDIVIDUELL',
        form.kU55Content.fK646_GemensamIndividuell,
        null,
        null,
        'COOPERATIVE_HOUSING.KU55.FK.HELP_TEXT.646_GEMENSAMINDIVIDUELL'
      ),
    });

    return this.fb.group({
      id: this._cf.createHidden(form.id),
      contact: this._cf.createMandatory('COMMON.CONTACT', form.contact),
      comment: this._cf.create('COMMON.NOTE', form.comment),
      formNumber: this._cf.createReadonly('CONTROL_DATA.FORM_NUMBER', form.formNumber),
      validationMessage: this._cf.create('Meddelande', form.validationMessage),
      kU55Content: this.contentForm,
    });
  }

  cancel() {
    this.navigation.navigateBack();
  }
}
